/**
 * AuditTrailTab - Paginated policy change history
 *
 * Displays per-field change entries with timestamp, user, change type,
 * section (the asset class or policy part, and the field), and before/after
 * values.
 */

import { useState } from 'react';
import { BodyText, Button, EmptyState, formatDateTime, TableScroll } from '@aaimplatform/ui';
import { ChevronLeft, ChevronRight, History } from 'lucide-react';
import type { components } from '@aaimplatform/institutional-types/generated/collateral-policy';

type PolicyAuditEntry = components['schemas']['PolicyAuditEntry'];

interface AuditTrailTabProps {
  entries: PolicyAuditEntry[];
  total: number;
  isLoading: boolean;
  onPageChange?: (offset: number) => void;
  /**
   * Display name per AssetClass key, from the policy on screen. A class the
   * policy no longer carries takes its name from its own audit rows.
   */
  classNames?: Readonly<Record<string, string>>;
}

const PAGE_SIZE = 25;

const CHANGE_TYPE_LABELS: Record<PolicyAuditEntry['changeType'], string> = {
  POLICY_CREATED: 'Policy Created',
  POLICY_UPDATED: 'Policy Updated',
  ASSET_CLASS_ADDED: 'Asset Class Added',
  ASSET_CLASS_UPDATED: 'Asset Class Updated',
  ASSET_CLASS_DISABLED: 'Asset Class Disabled',
  ASSET_CLASS_REMOVED: 'Asset Class Removed',
  CONCENTRATION_UPDATED: 'Concentration Updated',
  MONITORING_UPDATED: 'Monitoring Updated',
};

/**
 * Labels for the policy fields an audit entry can carry, worded as the policy
 * editors word them. A key the map does not name prints as-is rather than
 * being re-cased by hand.
 */
const FIELD_LABELS: Record<string, string> = {
  displayName: 'Display name',
  taxonomyNodeId: 'Asset class key',
  // Not an AssetClassPolicy property: it appears only in audit rows written
  // before NDFCU's policy version 2 dropped it (R1c2).
  taxonomyLevel: 'Taxonomy level (retired)',
  enabled: 'Accepted as collateral',
  maxLtv: 'Maximum LTV',
  discountRate: 'Discount rate',
  volatilityBuffer: 'Volatility buffer',
  volatilityThreshold: 'Volatility threshold',
  revaluationFrequency: 'Revaluation frequency',
  maxPriceAgeHours: 'Maximum price age (hours)',
  minLiquidityScore: 'Min liquidity score',
  chaThresholds: 'CHA thresholds',
  targetLtv: 'Green ceiling',
  yellowLtv: 'Yellow ceiling',
  orangeLtv: 'Orange ceiling',
  redLtv: 'Red threshold',
  actionWindows: 'Action windows',
  yellowReviewHours: 'Yellow review (hours)',
  orangeActionHours: 'Orange action (hours)',
  redEscalationHours: 'Red escalation (hours)',
  graceExtensionMaxHours: 'Grace extension max (hours)',
  performanceAdjustment: 'Performance adjustment',
  maxSinglePositionPct: 'Single position limit',
  maxSingleIssuerPct: 'Single issuer limit',
  maxSingleSectorPct: 'Single sector limit',
  maxSingleAssetClassPct: 'Single asset class limit',
  sectorOverrides: 'Sector overrides',
  alertChannels: 'Alert channels',
  escalationSequence: 'Escalation sequence',
  redTierHandoff: 'RED tier handoff',
  reportingFrequency: 'Reporting frequency',
  webhookConfig: 'Webhook configuration',
  url: 'URL',
  hmacSecret: 'HMAC secret',
};

/** Section roots as the policy editors name them. */
const SECTION_ROOT_LABELS: Record<string, string> = {
  assetClassPolicies: 'Asset classes',
  concentrationPolicy: 'Concentration',
  monitoringPolicy: 'Monitoring',
  policy: 'Whole policy',
};

function fieldLabel(key: string): string {
  return FIELD_LABELS[key] ?? SECTION_ROOT_LABELS[key] ?? key;
}

/**
 * The policy's sections in the order the editors show them. A whole-policy
 * value (the POLICY_CREATED row) prints one line per section, labeled as the
 * Section column labels it; until R1c2f4 it printed its stored keys raw, in
 * stored order.
 */
const SECTION_ORDER = ['assetClassPolicies', 'concentrationPolicy', 'monitoringPolicy'];

/** An object value's fields in display order: policy sections first, in editor order. */
function orderedEntries(value: Record<string, unknown>): Array<[string, unknown]> {
  const rank = (key: string) => {
    const i = SECTION_ORDER.indexOf(key);
    return i === -1 ? SECTION_ORDER.length : i;
  };
  return Object.entries(value).sort(([a], [b]) => rank(a) - rank(b));
}

/**
 * Keys whose values are secrets. The Monitoring tab stored a webhook HMAC
 * secret in the policy, and the diff recorded it as a previous and new value,
 * so an audit row could print it in the cell and its title (r3 fi-g7-1). The
 * server redacts new rows; rows written before that are masked here.
 */
const SECRET_KEYS = new Set(['hmacSecret']);

export const REDACTED = 'Redacted';

function isSecretKey(key: string | undefined): boolean {
  return key !== undefined && SECRET_KEYS.has(key);
}

/** The last segment of a dotted audit section ('monitoringPolicy.webhookConfig.hmacSecret'). */
function sectionLeaf(section: string | null | undefined): string | undefined {
  if (!section) return undefined;
  const parts = section.split('.');
  return parts[parts.length - 1];
}

/**
 * The value with every secret masked: the whole value when its own key is a
 * secret, otherwise any secret key at any depth.
 */
export function redactSecrets(value: unknown, ownKey?: string): unknown {
  if (value === null || value === undefined) return value;
  if (isSecretKey(ownKey)) return REDACTED;
  if (Array.isArray(value)) return value.map((v) => redactSecrets(v));
  if (typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([k, v]) => [k, redactSecrets(v, k)]),
    );
  }
  return value;
}

/**
 * The text for a value the audit row does not carry. A policy field that was
 * unset before a change, or a change with no section, is an absence; the cells
 * printed '--' for it until the spec-default-handling ruling (r3 fi-g7-12).
 */
export const NOT_RECORDED = 'Not recorded';

/**
 * The text for the absent side of an asset-class addition or removal. The
 * class was not in the policy on that side, which is a fact the row records;
 * the cells printed Not recorded for it (operator ruling 2026-10-02, R1c2d).
 */
export const NOT_IN_POLICY = 'Not in policy';

/**
 * The text for both sides of a save that changed no field. The server records
 * one POLICY_UPDATED entry for it, with no previous and no new value
 * (PolicyAuditEntry.changeType, operator ruling 2026-10-03).
 */
export const NO_CHANGE = 'No change';

/** What an absent value on one side of a row means: No change, Not in policy or Not recorded. */
export function absentText(
  changeType: PolicyAuditEntry['changeType'],
  side: 'previous' | 'new',
): string {
  if (changeType === 'POLICY_UPDATED') return NO_CHANGE;
  if (changeType === 'ASSET_CLASS_ADDED' && side === 'previous') return NOT_IN_POLICY;
  if (changeType === 'ASSET_CLASS_REMOVED' && side === 'new') return NOT_IN_POLICY;
  return NOT_RECORDED;
}

/** One value as text. Arrays of scalars join; nested objects collapse to a count. */
function formatScalar(value: unknown, absent: string = NOT_RECORDED): string {
  if (value === null || value === undefined) return absent;
  if (Array.isArray(value)) {
    return value.every((v) => typeof v !== 'object' || v === null)
      ? value.map((v) => String(v)).join(', ')
      : `${value.length} ${value.length === 1 ? 'entry' : 'entries'}`;
  }
  if (typeof value === 'object') {
    const n = Object.keys(value as Record<string, unknown>).length;
    return `${n} ${n === 1 ? 'field' : 'fields'}`;
  }
  return String(value);
}

/** One field's value as text. The asset-class section counts its classes. */
function formatField(key: string, value: unknown): string {
  if (key === 'assetClassPolicies' && value !== null && typeof value === 'object') {
    const n = Array.isArray(value) ? value.length : Object.keys(value as Record<string, unknown>).length;
    return `${n} ${n === 1 ? 'class' : 'classes'}`;
  }
  return formatScalar(value);
}

/** Plain-text form of a value, for the cell's title attribute. */
export function formatValue(value: unknown, absent: string = NOT_RECORDED): string {
  if (value === null || value === undefined) return absent;
  if (typeof value === 'object' && !Array.isArray(value)) {
    return orderedEntries(value as Record<string, unknown>)
      .map(([k, v]) => `${fieldLabel(k)}: ${formatField(k, v)}`)
      .join('; ');
  }
  return formatScalar(value);
}

/**
 * An audit value as field/value pairs. An object-valued change printed as a
 * JSON string before (fi-g7-16); a scalar still prints as itself.
 */
function AuditValue({ value, absent = NOT_RECORDED }: { value: unknown; absent?: string }) {
  if (value === null || value === undefined) return <span>{absent}</span>;
  if (typeof value === 'object' && !Array.isArray(value)) {
    const entries = orderedEntries(value as Record<string, unknown>);
    if (entries.length === 0) return <span>{NOT_RECORDED}</span>;
    return (
      <dl className="space-y-0.5">
        {entries.map(([k, v]) => (
          <div key={k} className="flex gap-1">
            <dt className="font-sans text-tertiary">{fieldLabel(k)}:</dt>
            <dd className="font-mono">{formatField(k, v)}</dd>
          </div>
        ))}
      </dl>
    );
  }
  return <span className="font-mono">{formatScalar(value)}</span>;
}

/**
 * A dotted audit section as what changed and where: the asset class (or the
 * policy part) and the field path inside it. The cell printed the raw path,
 * truncated at 200px, so a class key and its field did not fit together
 * (operator ruling 2026-10-02, R1c2d).
 */
export function describeSection(
  section: string | null | undefined,
  classNames: Readonly<Record<string, string>> = {},
): { owner: string; field?: string } | undefined {
  if (!section) return undefined;
  const [root, ...rest] = section.split('.');
  if (root === 'assetClassPolicies' && rest.length > 0) {
    const [classKey, ...path] = rest;
    return {
      owner: classNames[classKey] ?? classKey,
      ...(path.length > 0 ? { field: path.map(fieldLabel).join(' / ') } : {}),
    };
  }
  return {
    owner: SECTION_ROOT_LABELS[root] ?? fieldLabel(root),
    ...(rest.length > 0 ? { field: rest.map(fieldLabel).join(' / ') } : {}),
  };
}

/** The display name an audit value carries, when it is an asset-class policy. */
function displayNameOf(value: unknown): string | undefined {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return undefined;
  const name = (value as Record<string, unknown>).displayName;
  return typeof name === 'string' && name.trim() !== '' ? name : undefined;
}

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * The server writes the literal 'unknown' when a change carries no user
 * (collateral-policy.routes.ts). That is an absence, and prints as one.
 *
 * The route records the token's email claim and falls back to its subject id.
 * Rows written before it read the email carry only the id, which names no
 * person: the cell labels it as an id, and its title keeps the full value
 * (R1c2f4).
 */
export function formatChangedBy(changedBy: string): string {
  const value = changedBy.trim();
  if (value === '' || value === 'unknown') return NOT_RECORDED;
  if (UUID_PATTERN.test(value)) return `User ID ${value.slice(0, 8)}`;
  return value;
}


export function AuditTrailTab({ entries, total, isLoading, onPageChange, classNames = {} }: AuditTrailTabProps) {
  const [page, setPage] = useState(0);
  const totalPages = Math.ceil(total / PAGE_SIZE);

  // Names from the rows themselves first, so a class the current policy no
  // longer carries keeps its name; the policy on screen wins where it has one.
  const names: Record<string, string> = {};
  for (const entry of entries) {
    const [root, classKey] = (entry.section ?? '').split('.');
    if (root !== 'assetClassPolicies' || !classKey) continue;
    const name = displayNameOf(entry.newValue) ?? displayNameOf(entry.previousValue);
    if (name) names[classKey] = name;
  }
  Object.assign(names, classNames);

  const handlePrev = () => {
    const newPage = Math.max(0, page - 1);
    setPage(newPage);
    onPageChange?.(newPage * PAGE_SIZE);
  };

  const handleNext = () => {
    const newPage = Math.min(totalPages - 1, page + 1);
    setPage(newPage);
    onPageChange?.(newPage * PAGE_SIZE);
  };

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-3">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="h-16 bg-elevated rounded" />
        ))}
      </div>
    );
  }

  if (entries.length === 0) {
    return (
      <div className="space-y-6">
        {/* Page title lives in the sticky topnav (AppHeader); no redundant H1. */}
        <EmptyState
          icon={<History className="h-8 w-8" />}
          title="No policy changes"
          description="Policy changes will appear here after the first save."
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Page title lives in the sticky topnav (AppHeader); no redundant H1. */}

      <div className="flex items-center justify-between">
        <BodyText variant="ui" size="sm" color="secondary">
          {total} change{total !== 1 ? 's' : ''} recorded
        </BodyText>
        {totalPages > 1 && (
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={handlePrev}
              disabled={page === 0}
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <BodyText variant="ui" size="sm">
              Page {page + 1} of {totalPages}
            </BodyText>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleNext}
              disabled={page >= totalPages - 1}
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        )}
      </div>

      <div className="border border-recessed rounded-sm overflow-hidden">
        <TableScroll label="Policy audit trail">
        <table className="w-full text-sm min-w-table-wide">
          <thead>
            <tr className="bg-elevated border-b border-recessed">
              <th className="text-left px-4 py-3 font-medium text-secondary">Timestamp</th>
              <th className="text-left px-4 py-3 font-medium text-secondary">User</th>
              <th className="text-left px-4 py-3 font-medium text-secondary">Change Type</th>
              <th className="text-left px-4 py-3 font-medium text-secondary">Section</th>
              <th className="text-left px-4 py-3 font-medium text-secondary">Previous</th>
              <th className="text-left px-4 py-3 font-medium text-secondary">New</th>
            </tr>
          </thead>
          <tbody>
            {entries.map((entry) => {
              const leaf = sectionLeaf(entry.section);
              const previousValue = redactSecrets(entry.previousValue, leaf);
              const newValue = redactSecrets(entry.newValue, leaf);
              const previousAbsent = absentText(entry.changeType, 'previous');
              const newAbsent = absentText(entry.changeType, 'new');
              const where = describeSection(entry.section, names);
              return (
              <tr key={entry.entryId} className="border-b border-recessed last:border-b-0 hover:bg-elevated/50">
                {/* The kit formatter carries the time zone; toLocaleString
                    printed a time with no zone (r3 fi-g7-16). */}
                <td className="px-4 py-3 font-mono text-xs whitespace-nowrap">
                  {formatDateTime(entry.changedAt)}
                </td>
                <td className="px-4 py-3 truncate max-w-[160px]" title={entry.changedBy}>
                  {formatChangedBy(entry.changedBy)}
                </td>
                <td className="px-4 py-3">
                  <span className="inline-block px-2 py-0.5 bg-elevated rounded text-xs font-medium">
                    {CHANGE_TYPE_LABELS[entry.changeType] ?? entry.changeType}
                  </span>
                </td>
                {/* Wraps; never truncates. The raw path stays in the title. */}
                <td className="px-4 py-3 text-xs align-top max-w-[240px] break-words" title={entry.section ?? ''}>
                  {where ? (
                    <div className="space-y-0.5">
                      <div className="font-medium">{where.owner}</div>
                      {where.field && <div className="text-tertiary">{where.field}</div>}
                    </div>
                  ) : (
                    NOT_RECORDED
                  )}
                </td>
                <td className="px-4 py-3 text-xs align-top max-w-[240px] break-words" title={formatValue(previousValue, previousAbsent)}>
                  <AuditValue value={previousValue} absent={previousAbsent} />
                </td>
                <td className="px-4 py-3 text-xs align-top max-w-[240px] break-words" title={formatValue(newValue, newAbsent)}>
                  <AuditValue value={newValue} absent={newAbsent} />
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
        </TableScroll>
      </div>
    </div>
  );
}
